import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Card, Button, message, Empty, notification, Modal } from 'antd';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import TableFull from '../TableFull/TableFull';
import InfiniteScroll from '../InfiniteScroll/InfiniteScroll';
import Spinner from '../spinner/Spinner';
import EnvelopeIcon from '../../assets/dashboard/EnvelopeIcon';
import { ScoreColumn } from './ScoreColumn';
import EditEmailTemplate from '../EditEmailTemplate/EditEmailTemplate';
import {
  getUnreviewedEvaluations,
  markEvaluationsAsRead,
} from '../../API/services/partnerToCandidateService';
import { postSendEmailCustom } from '../../API/services/screeningService';
import { t } from '../../i18n/i18n';
import useTranslations from '../../hooks/useTranslations';
import './UnreviewedEvaluations.scss';

const UnreviewedEvaluations = () => {
  const currentWorkspace = useSelector((state) => state.auth.currentWorkspace, shallowEqual);
  const { template, subjectEmail } = useSelector((state) => state.screenings);
  const { key } = useTranslations();
  const history = useHistory();
  const scrollContainerRef = useRef(null);

  const [elements, setElements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalUnreviewed, setTotalUnreviewed] = useState(0);
  const [editMailVisible, setEditMailVisible] = useState(false);
  const itemsPerPage = 10;

  const getEvaluationDate = (record) => {
    if (record.endDate)
      return { date: record.endDate, label: t('UNREVIEWED_EVALUATIONS_STATUS_FINISHED') };
    if (record.startDate)
      return { date: record.startDate, label: t('UNREVIEWED_EVALUATIONS_STATUS_STARTED') };
    if (record.acceptedAt)
      return { date: record.acceptedAt, label: t('UNREVIEWED_EVALUATIONS_STATUS_ACCEPTED') };
    return { date: null, label: '' };
  };

  const handleGoToCandidate = (profileId, recruitSessionId) => {
    if (recruitSessionId && profileId) {
      history.push(`/search/${recruitSessionId}/profiles/${profileId}`);
    }
  };

  const mapDataToElements = (items) =>
    items.map((item) => ({
      ...item,
      key: item.id,
      fullName: item.candidate.fullName,
      email: item.candidate.email,
      screeningName: item.evaluation.screeningName,
      score: item.evaluation.score,
      acceptedAt: item.evaluation.acceptedAt,
      startDate: item.evaluation.startDate,
      endDate: item.evaluation.endDate,
      createdAt: new Date(item.createdAt).toLocaleDateString('es-AR'),
      checked: false,
      candidate: (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id={`tooltip-candidate-${item.id}`}>{item.candidate.fullName}</Tooltip>}
        >
          <div
            className="candidate-cell cursor-pointer"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleGoToCandidate(item.candidate.profileId, item.evaluation.recruitSessionId);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                e.preventDefault();
                handleGoToCandidate(item.candidate.profileId, item.evaluation.recruitSessionId);
              }
            }}
          >
            <div className="candidate-info">
              <span className="candidate-name">{item.candidate.fullName}</span>
              <span className="candidate-email">{item.candidate.email}</span>
            </div>
          </div>
        </OverlayTrigger>
      ),
      evaluation: (() => {
        const dateInfo = getEvaluationDate(item.evaluation);
        return (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`tooltip-evaluation-${item.id}`}>
                {item.evaluation.screeningName}
              </Tooltip>
            }
          >
            <div
              className="evaluation-info cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (item.evaluation.recruitSessionId) {
                  history.push(`/search/${item.evaluation.recruitSessionId}`);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  e.preventDefault();
                  if (item.evaluation.recruitSessionId) {
                    history.push(`/search/${item.evaluation.recruitSessionId}`);
                  }
                }
              }}
            >
              <span className="evaluation-name">{item.evaluation.screeningName}</span>
              {dateInfo.date && (
                <span className="evaluation-date">
                  {dateInfo.label}
                  {t('UNREVIEWED_EVALUATIONS_DATE_PREFIX')}
                  {new Date(dateInfo.date).toLocaleDateString('es-AR')}
                </span>
              )}
            </div>
          </OverlayTrigger>
        );
      })(),
      avgScore: ScoreColumn(item.evaluation.score ?? -1, false),
    }));

  const isMountedRef = useRef(true);

  const fetchUnreviewedEvaluations = async (page = 1, append = false) => {
    if (!currentWorkspace) return;

    if (isMountedRef.current) {
      setIsLoading(true);
    }
    try {
      const data = await getUnreviewedEvaluations(currentWorkspace, page, itemsPerPage);
      const mappedData = mapDataToElements(data.collection);

      if (isMountedRef.current) {
        setElements((prev) => (append ? [...prev, ...mappedData] : mappedData));

        const totalFromBackend = data.pagination.total;
        const currentOffset = data.pagination.offset;
        const hasMoreData = currentOffset + data.collection.length < totalFromBackend;

        setHasMore(hasMoreData);
        setTotalUnreviewed(totalFromBackend);
        setCurrentPage(page);
      }
    } catch (error) {
      if (isMountedRef.current) {
        notification.error({
          message: t('UNREVIEWED_EVALUATIONS_ERROR_LOADING'),
          placement: 'topRight',
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (currentWorkspace) {
      fetchUnreviewedEvaluations(1, false);
    }
  }, [currentWorkspace]);

  useEffect(() => {
    const cardBody = document.querySelector('#unreviewed-evaluations .ant-card-body');
    if (cardBody) {
      scrollContainerRef.current = cardBody;
    }
  }, []);

  const loadMoreData = useCallback(() => {
    if (isLoading || !hasMore) return;
    fetchUnreviewedEvaluations(currentPage + 1, true);
  }, [isLoading, hasMore, currentPage]);

  const headers = [
    {
      columnOrder: 1,
      key: 'candidate',
      name: t('UNREVIEWED_EVALUATIONS_HEADER_CANDIDATE'),
      width: '30%',
      checked: true,
    },
    {
      columnOrder: 2,
      key: 'createdAt',
      name: t('UNREVIEWED_EVALUATIONS_HEADER_DATE'),
      width: '12%',
      checked: true,
    },
    {
      columnOrder: 3,
      key: 'evaluation',
      name: t('UNREVIEWED_EVALUATIONS_HEADER_EVALUATION'),
      width: '48%',
      checked: true,
    },
    {
      columnOrder: 4,
      key: 'avgScore',
      name: t('UNREVIEWED_EVALUATIONS_HEADER_SCORE'),
      width: '10%',
      checked: true,
    },
  ];

  const quantity = useMemo(() => elements?.filter((item) => item?.checked)?.length, [elements]);

  const onCheckRow = (newElements) => {
    setElements(newElements);
  };

  const handleSendMail = () => {
    const selectedItems = elements.filter((item) => item?.checked);

    if (selectedItems.length > 0) {
      setEditMailVisible(true);
    }
  };

  const handleSendEmail = async (error, setLoading, emailsToSend, ccEmailsToSend) => {
    const selectedItems = elements.filter((item) => item?.checked);

    if (selectedItems.length === 0) {
      setEditMailVisible(false);
      return;
    }

    const assessmentInvitationIds = selectedItems
      .map((item) => item.assessmentInvitationId)
      .filter((id) => id);

    if (assessmentInvitationIds.length === 0) {
      notification.open({
        message: t('UNREVIEWED_EVALUATIONS_ERROR_MARK_READ'),
        icon: <i className="fas fa-times" style={{ color: 'red' }} />,
      });
      setEditMailVisible(false);
      return;
    }

    const requestData = {
      invitationsIds: assessmentInvitationIds,
      emailType: 'CUSTOM_GENERIC',
      emailParams: {
        subject: subjectEmail.subjectEmail,
        html: template.template,
      },
      customEmailId: template.id,
      language: key,
      ...(ccEmailsToSend?.length && { cc: ccEmailsToSend }),
    };

    try {
      await postSendEmailCustom(requestData, {});
      notification.open({
        message: t('INVITED_SINGLE_PROFILE_CORRECTLY'),
        icon: <i className="fas fa-check" style={{ color: '#58DDA6' }} />,
      });
      setEditMailVisible(false);
    } catch (error) {
      notification.open({
        message: t('INVITATION_ERROR'),
        icon: <i className="fas fa-times" style={{ color: 'red' }} />,
      });
    }
  };

  const handleMarkAsRead = async () => {
    const selectedItems = elements.filter((item) => item?.checked);

    if (selectedItems.length === 0) return;

    try {
      const ids = selectedItems.map((item) => item.id);
      await markEvaluationsAsRead(ids);

      const remainingElements = elements.filter((item) => !item.checked);
      const newTotal = totalUnreviewed - selectedItems.length;

      setElements(remainingElements);
      setTotalUnreviewed(newTotal);

      if (
        remainingElements.length < itemsPerPage &&
        hasMore &&
        newTotal > remainingElements.length
      ) {
        fetchUnreviewedEvaluations(currentPage + 1, true);
      }

      message.success(`${selectedItems.length} ${t('UNREVIEWED_EVALUATIONS_MARKED_AS_READ')}`);
    } catch (error) {
      message.error(t('UNREVIEWED_EVALUATIONS_ERROR_MARK_READ'));
    }
  };

  const handleGoToCandidates = () => {
    history.push('/users/summary');
  };

  const remainingCount = totalUnreviewed - elements.length;

  return (
    <>
      <div className="evaluations-card-wrapper">
        <Card
          id="UNREVIEWED_EVALUATIONS"
          title={`${t('UNREVIEWED_EVALUATIONS_TITLE')} (${totalUnreviewed})`}
          className="evaluations-card"
          bordered={false}
        >
          <div className="unreviewed-toolbar d-flex justify-content-between mb-0 align-items-center mt-2 mb-2 px-3">
            <div className="d-flex flex-row align-items-center">
              {quantity > 0 ? (
                <div>
                  <strong className="mr-1 text-xs total-number-text font-weight-bold">
                    {quantity}
                  </strong>
                  <span className="mr-2 text-xs gray-subtitle font-weight-600">
                    {t('UNREVIEWED_EVALUATIONS_SELECTED_CANDIDATES')}
                  </span>
                </div>
              ) : (
                <div className="text-xs gray-subtitle">
                  {t('UNREVIEWED_EVALUATIONS_SELECT_TO_ACTION')}
                </div>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              <Button
                type="primary"
                size="small"
                className="rounded-0 text-xs"
                onClick={handleSendMail}
                disabled={quantity === 0}
                icon={<EnvelopeIcon fill={quantity === 0 ? '#999' : '#FFF'} aria-hidden="true" />}
              >
                {t('UNREVIEWED_EVALUATIONS_SEND_EMAIL')}
              </Button>
              <Button
                size="small"
                className="rounded-0 text-xs"
                onClick={handleMarkAsRead}
                disabled={quantity === 0}
              >
                {t('UNREVIEWED_EVALUATIONS_MARK_AS_READ')}
              </Button>
            </div>
          </div>
          {!isLoading && elements.length === 0 ? (
            <Empty
              description={t('UNREVIEWED_EVALUATIONS_EMPTY')}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <>
              <TableFull
                elements={elements}
                headers={headers}
                onCheckRow={onCheckRow}
                onCheckAllRow={onCheckRow}
                checkbox
              />
              {isLoading && <Spinner show />}
              <InfiniteScroll
                onScrollEnd={loadMoreData}
                isLoading={isLoading}
                containerRef={scrollContainerRef}
              />
            </>
          )}
        </Card>
        {remainingCount > 0 && (
          <div className="unreviewed-footer">
            <span className="unreviewed-footer-text">
              {t('UNREVIEWED_EVALUATIONS_MORE')} {remainingCount}{' '}
              {t('UNREVIEWED_EVALUATIONS_EVALUATIONS')} (
              <span
                className="link-candidates"
                role="button"
                tabIndex={0}
                onClick={handleGoToCandidates}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleGoToCandidates();
                  }
                }}
              >
                {t('UNREVIEWED_EVALUATIONS_GO_TO_CANDIDATES')}
              </span>
              )
            </span>
          </div>
        )}
      </div>
      <Modal
        title={t('UNREVIEWED_EVALUATIONS_SEND_EMAIL')}
        open={editMailVisible}
        onCancel={() => setEditMailVisible(false)}
        footer={null}
        width={1200}
        className="unreviewed-evaluations-email-modal"
        destroyOnClose
      >
        <EditEmailTemplate
          handleSendRequest={handleSendEmail}
          goBack={() => setEditMailVisible(false)}
          viewInfo={false}
          emailDashboard
          emails={elements.filter((item) => item?.checked).map((item) => item.email)}
        />
      </Modal>
    </>
  );
};

export default UnreviewedEvaluations;
